import { supabase } from '../../lib/supabase';
import { initiateCampayShopEdgePayment, checkCampayShopEdgePaymentStatus } from '../campayShopEdgeService';
import { processPayment } from '../paymentService';

interface OrderPaymentOptions {
  paymentMethod: string;
  phoneNumber?: string;
}

interface Order {
  id: string;
  order_number: string;
  total_amount: number;
  customer_name: string;
  customer_email: string;
}

/**
 * Initialise un paiement pour une commande en utilisant le service spécifique à la boutique
 */
export const initiateOrderPayment = async (order: Order, options: OrderPaymentOptions) => {
  try {
    // Vérifier si la méthode de paiement est MTN ou Orange (Campay)
    if (['mtn', 'orange'].includes(options.paymentMethod)) {
      // Utiliser le service dédié pour les paiements de la boutique
      const response = await initiateCampayShopEdgePayment({
        amount: order.total_amount,
        phoneNumber: options.phoneNumber || '',
        operator: options.paymentMethod,
        order_id: order.id,
        order_number: order.order_number,
        customerName: order.customer_name,
        customerEmail: order.customer_email,
        description: `Commande #${order.order_number} - Boutique`
      });
      
      // Mettre à jour la commande avec les informations de paiement
      await supabase
        .from('shop_orders')
        .update({ 
          payment_reference: response.payment_reference,
          payment_method: options.paymentMethod,
          payment_url: response.payment_url,
          status: 'pending_payment' // Mettre à jour le statut de la commande
        })
        .eq('id', order.id);
      
      return response;
    } else {
      // Pour les autres méthodes de paiement, utiliser le système existant
      const paymentData = {
        amount: order.total_amount,
        votes_count: 1, // Valeur requise par l'API existante
        paymentMethod: options.paymentMethod,
        phoneNumber: options.phoneNumber,
        contestantId: '00000000-0000-0000-0000-000000000000', // ID générique pour les transactions hors votes
        customerName: order.customer_name,
        customerEmail: order.customer_email,
        description: `Commande #${order.order_number} - Boutique`
      };

      const response = await processPayment(paymentData);
      
      // Mettre à jour la commande avec les informations de paiement
      await supabase
        .from('shop_orders')
        .update({ 
          payment_reference: response.payment_reference,
          payment_method: options.paymentMethod,
          payment_url: response.payment_url,
          status: 'pending_payment' // Mettre à jour le statut de la commande
        })
        .eq('id', order.id);
      
      return response;
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du paiement:', error);
    throw new Error(`Erreur lors de l'initialisation du paiement: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};

/**
 * Vérifie le statut d'un paiement pour une commande en utilisant la fonction edge dédiée
 */
export const checkOrderPaymentStatus = async (orderId: string) => {
  try {
    // Récupérer les informations de la commande
    const { data: order, error } = await supabase
      .from('shop_orders')
      .select('payment_reference, payment_method')
      .eq('id', orderId)
      .single();
    
    if (error || !order || !order.payment_reference || !order.payment_method) {
      throw new Error('Commande introuvable ou informations de paiement manquantes');
    }
    
    // Utiliser le service dédié pour vérifier le statut du paiement de la boutique
    const paymentStatus = await checkCampayShopEdgePaymentStatus(order.payment_reference, orderId);
    return paymentStatus;
  } catch (error) {
    console.error('Erreur lors de la vérification du statut de paiement:', error);
    throw new Error(`Erreur lors de la vérification du paiement: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};

/**
 * Marque une commande comme payée (pour les tests ou les paiements manuels)
 */
export const markOrderAsPaid = async (orderId: string) => {
  try {
    const { error } = await supabase
      .from('shop_orders')
      .update({ status: 'paid' })
      .eq('id', orderId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut de la commande:', error);
    throw new Error(`Erreur lors de la mise à jour: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};
